import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Shield, 
  ArrowLeft, 
  User, 
  Clock, 
  AlertTriangle, 
  FileText, 
  ExternalLink,
  Activity,
  Lock,
  Flag,
  MoreVertical,
  CheckCircle2
} from 'lucide-react';
import { dbService, VettingProfile } from '../services/dbService';
import { auth } from '../lib/firebase';
import { cn } from '../lib/utils';

export default function DossierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState<VettingProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDossier = async () => {
      if (!id) return;
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const data = await dbService.getVettingProfile(user.uid, id);
        if (data) {
          setDossier(data);
        } else {
          setError("Subject Dossier Not Found or Restricted");
        }
      } catch (err) {
        console.error("Dossier retrieval failure:", err);
        setError("Systemic failure during dossier decryption");
      } finally {
        setLoading(false);
      }
    };

    fetchDossier();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-t-2 border-gold rounded-full animate-spin mb-4"></div>
          <span className="text-[10px] uppercase tracking-widest text-gold animate-pulse font-bold">Decrypting Subject Data...</span>
        </div>
      </div>
    );
  }

  if (error || !dossier) {
    return (
      <div className="min-h-screen bg-theme-bg flex flex-col items-center justify-center p-6 text-center">
        <Lock className="h-12 w-12 text-red-500/50 mb-6" />
        <h2 className="text-2xl font-serif italic text-theme-text mb-4">{error || "Access Denied"}</h2>
        <Link to="/dashboard" className="text-gold uppercase tracking-widest text-xs font-bold hover:underline">Return to Secure Dashboard</Link>
      </div>
    );
  }

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Verified': return 'text-green-500';
      case 'Restricted': return 'text-red-500';
      case 'Testing': return 'text-gold';
      default: return 'text-taupe';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'text-green-500';
      case 'Medium': return 'text-gold';
      case 'High': return 'text-orange-500';
      case 'Critical': return 'text-red-600';
      default: return 'text-taupe';
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg pt-20">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Navigation Breadcrumb */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center space-x-2 text-taupe hover:text-gold transition-colors text-[10px] uppercase tracking-widest font-bold mb-12"
        >
          <ArrowLeft className="h-3 w-3" />
          <span>Back to Executive Suite</span>
        </Link>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Dossier Content */}
          <div className="lg:col-span-2 space-y-12">
            <header className="border-b border-theme-border pb-8">
              <div className="flex justify-between items-start">
                <div>
                  <span className="kicker mb-4">Subject Profile // Dossier ID: {id?.slice(0, 8)}</span>
                  <h1 className="text-4xl md:text-6xl mb-4">{dossier.name}</h1>
                  <div className="flex items-center space-x-6 text-[10px] uppercase tracking-widest font-bold">
                    <span className={cn("flex items-center gap-2", getStageColor(dossier.stage))}>
                       <Shield className="h-3 w-3" /> {dossier.stage} Status
                    </span>
                    <span className="text-taupe">|</span>
                    <span className={cn("flex items-center gap-2", getRiskColor(dossier.riskLevel))}>
                       <Flag className="h-3 w-3" /> {dossier.riskLevel} Risk
                    </span>
                  </div>
                </div>
                <button className="p-3 border border-theme-border hover:border-gold/50 transition-colors">
                  <MoreVertical className="h-5 w-5 text-taupe" />
                </button>
              </div>
            </header>

            {/* Assessment Narrative */}
            <section className="bg-black/20 p-8 md:p-12 border border-theme-border/50 relative overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-5">
                  <FileText className="h-32 w-32" />
               </div>
               <h2 className="text-xl font-serif italic mb-8 flex items-center gap-3">
                  <Activity className="h-5 w-5 text-gold" />
                  Psychological Narrative
               </h2>
               <div className="prose prose-invert max-w-none">
                  <p className="text-taupe leading-relaxed text-lg whitespace-pre-wrap italic">
                    "{dossier.notes || "No qualitative data entered during initial intake. Further observation required."}"
                  </p>
               </div>
            </section>

            {/* Behavioral Indicators Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-theme-border/50 p-8 hover:border-gold/20 transition-all group">
                <h3 className="text-xs uppercase tracking-widest text-taupe font-bold mb-6 flex justify-between">
                  Integrity Markers
                  <CheckCircle2 className="h-4 w-4 text-gold/30 group-hover:text-gold transition-colors" />
                </h3>
                <ul className="space-y-4 text-sm italic text-theme-text/80">
                  <li className="flex items-start gap-3">
                    <div className="h-1 w-1 rounded-full bg-gold mt-2 shrink-0"></div>
                    <span>Consistency in verbal narratives vs behavioral output.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="h-1 w-1 rounded-full bg-gold mt-2 shrink-0"></div>
                    <span>Reaction to personal accountability triggers.</span>
                  </li>
                </ul>
              </div>
              <div className="border border-theme-border/50 p-8 hover:border-gold/20 transition-all group">
                <h3 className="text-xs uppercase tracking-widest text-taupe font-bold mb-6 flex justify-between">
                  Insecurity Leaks
                  <AlertTriangle className="h-4 w-4 text-red-500/30 group-hover:text-red-500 transition-colors" />
                </h3>
                <ul className="space-y-4 text-sm italic text-theme-text/80">
                  <li className="flex items-start gap-3">
                    <div className="h-1 w-1 rounded-full bg-red-500 mt-2 shrink-0"></div>
                    <span>Status posturing under social pressure environments.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="h-1 w-1 rounded-full bg-red-500 mt-2 shrink-0"></div>
                    <span>Need for external validation in professional contexts.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar Metadata */}
          <aside className="space-y-12">
            <div className="bg-gold/5 border border-gold/20 p-8 backdrop-blur-sm">
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-8 flex items-center gap-2">
                <Lock className="h-3 w-3" /> System Metadata
              </h3>
              <div className="space-y-6">
                <div className="flex justify-between items-center text-[10px] uppercase tracking-widest">
                  <span className="text-taupe">Created</span>
                  <span className="text-theme-text">{dossier.createdAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] uppercase tracking-widest">
                  <span className="text-taupe">Last Evaluated</span>
                  <span className="text-theme-text">{new Date().toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] uppercase tracking-widest">
                  <span className="text-taupe">Author</span>
                  <span className="text-gold">{auth.currentUser?.displayName || 'Executive Prime'}</span>
                </div>
              </div>
              <div className="h-[1px] bg-gold/10 my-8"></div>
              <p className="text-[10px] italic text-taupe leading-relaxed text-center">
                This dossier is protected under the Systemic Boundaries Architecture. Unauthorized disclosure is a breach of Executive Protocol.
              </p>
            </div>

            <div className="border border-theme-border p-8 space-y-6">
               <h3 className="text-[10px] uppercase tracking-widest text-taupe font-bold flex items-center gap-2">
                  <Activity className="h-3 w-3" /> Suggested Protocols
               </h3>
               <div className="space-y-4">
                  <div className="p-4 bg-black/40 border-l-2 border-gold text-[11px] italic text-theme-text">
                    "Maintain strategic silence for 48 hours to observe pursuit patterns."
                  </div>
                  <div className="p-4 bg-black/40 border-l-2 border-gold text-[11px] italic text-theme-text">
                    "Audit social media proximity markers for boundary leaks."
                  </div>
               </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
